import { getLocaleString } from '@assets/i18n/config';
import type { CurrencyCodes } from '@generated/graphql';
import type { Breach, BreachType, DriftAccount } from '@interfaces/driftQueue';
import PublicIcon from '@mui/icons-material/Public';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useTheme as useMuiTheme } from '@mui/material';
import type { ColumnDef } from '@ovComponents/2-component/dataTable';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { UserContext } from '@providers/userContextProvider';
import { currencySymbol } from '@utils/currency';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import { Box, Tooltip, Typography, useTheme } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Returns the breach with the lowest `severity` integer (highest severity).
 * Returns `null` when the array is empty.
 */
export function getHighestSeverityBreach(breaches: Breach[]): Breach | null {
  if (breaches.length === 0) return null;
  return breaches.reduce((highest, current) => {
    return current.severity < highest.severity ? current : highest;
  });
}

/**
 * Counts unique breach reasons (deduplicates exact `reason` matches).
 */
export function getDistinctReasonCount(breaches: Breach[]): number {
  return new Set(breaches.map((b) => b.reasonKey ?? b.reason)).size;
}

export function formatBreachReason(
  breach: Breach,
  t: TFunction<['driftQueue']>,
): string {
  if (breach.reasonKey) {
    return t(`driftQueue:breaches.${breach.reasonKey}`, {
      count: breach.reasonCount ?? 0,
    });
  }
  return breach.reason;
}

/**
 * Maps a `BreachType` to sys.color tokens.
 * Centralised here so changing the token mapping is a one-line edit.
 */
export type TokenForBreachFn = (type: BreachType) => string;

export function useDriftQueueColumns(): ColumnDef<DriftAccount, unknown>[] {
  const { t } = useTranslation(['driftQueue', 'accountTypes']);
  const muiTheme = useMuiTheme();
  const theme = useTheme();
  const { activeCurrency } = useContext(UserContext);
  const currencyCode = activeCurrency || 'CAD';

  /** Background color for the AssetProgressBar segments. */
  const tokenForBreach: TokenForBreachFn = (type) =>
    type === 'single'
      ? (theme.color.warning ?? muiTheme.palette.warning.main)
      : (theme.color.negative ?? muiTheme.palette.error.main);

  /** Colors for the breach reason pill chip. */
  const chipColors = (type: BreachType) =>
    type === 'single'
      ? { bg: theme.color.warningVariant, text: theme.color.onWarningVariant }
      : {
          bg: theme.color.negativeVariant,
          text: theme.color.onNegativeVariant,
        };

  const columns: ColumnDef<DriftAccount, unknown>[] = [
    {
      id: 'portfolio',
      accessorFn: (row) => row.portfolioName,
      header: t('driftQueue:table.portfolio'),
      enableSorting: true,
      cell: ({ row }) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {row.original.portfolioName}
            </Typography>
            {row.original.isStale && (
              <Tooltip
                title={t('driftQueue:table.staleTooltip', {
                  date: row.original.asOfDate,
                })}
              >
                <ReportProblemIcon
                  aria-label={String(t('driftQueue:legend.stale'))}
                  sx={{ fontSize: 14, color: muiTheme.palette.warning.dark }}
                />
              </Tooltip>
            )}
            {row.original.hasInternational && (
              <Tooltip title={t('driftQueue:legend.international')}>
                <PublicIcon
                  aria-label={String(t('driftQueue:legend.international'))}
                  sx={{ fontSize: 14, color: theme.color.onSurfaceVariant }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant='labelSmall' color='textSecondary'>
            {row.original.accountNumber}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'household',
      accessorKey: 'householdName',
      header: t('driftQueue:table.household'),
      enableSorting: false,
    },
    {
      id: 'accountType',
      accessorKey: 'accountType',
      header: t('driftQueue:table.accountType'),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: theme.color.primaryVariant,
            // Keep the pill on a single line — long values (e.g. "Non-Registered")
            // otherwise wrap to 2 lines, making that row taller than its
            // neighbours. Because the active filter/sort changes which values are
            // visible, the inconsistent wrapping read as "dense breaks on click".
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: theme.color.onPrimaryVariant, whiteSpace: 'nowrap' }}
          >
            {t(`accountTypes:${getValue<string>()}`, {
              defaultValue: getValue<string>(),
            })}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reason',
      accessorFn: (row) => getHighestSeverityBreach(row.breaches)?.reason ?? '',
      header: t('driftQueue:table.reason'),
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const topBreach = getHighestSeverityBreach(row.original.breaches);
        if (!topBreach) return null;
        const distinctCount = getDistinctReasonCount(row.original.breaches);
        const { bg, text } = chipColors(row.original.breachType);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* Solid-color reason pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: 10,
                backgroundColor: bg,
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 13, color: text }} />
              <Typography
                variant='labelSmall'
                sx={{ color: text, fontWeight: 600 }}
              >
                {formatBreachReason(topBreach, t)}
              </Typography>
            </Box>

            {/* Extra alert count badge */}
            {distinctCount > 1 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: bg,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{ color: text, fontWeight: 700 }}
                >
                  {distinctCount}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'custodian',
      accessorKey: 'custodian',
      header: t('driftQueue:table.custodian'),
      enableSorting: true,
    },
    {
      id: 'portfolioValue',
      accessorKey: 'portfolioValue',
      header: t('driftQueue:table.value'),
      enableSorting: true,
      meta: { dataTable: { align: 'right', numeric: true } },
      cell: ({ getValue }) => {
        const locale = getLocaleString();
        const value = getValue<number>();
        const symbol = currencySymbol[currencyCode as CurrencyCodes] ?? '$';
        const formatted = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Math.abs(value));
        const sign = value < 0 ? '-' : '';
        return isCurrencySymbolAfter(locale)
          ? `${sign}${formatted} ${symbol}`
          : `${sign}${symbol}${formatted}`;
      },
    },
    {
      id: 'cashAndPosition',
      accessorFn: (row) => row.cashPositionPercentage,
      header: t('driftQueue:table.cashAndPosition'),
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const barColor = tokenForBreach(row.original.breachType);
        return (
          <Box sx={{ minWidth: 110 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.25,
              }}
            >
              <Typography variant='labelSmall' color='textSecondary'>
                {t('driftQueue:table.cashShort')}
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{ color: barColor, fontWeight: 700 }}
              >
                +{row.original.cashPositionPercentage.toFixed(1)}%
              </Typography>
            </Box>
            <AssetProgressBar
              data={[
                {
                  key: 'cash',
                  percentage: row.original.cashPositionPercentage,
                  colorCode: barColor,
                },
              ]}
              sx={{ mb: 0.5 }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.25,
              }}
            >
              <Typography variant='labelSmall' color='textSecondary'>
                {t('driftQueue:table.positionShort')}
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{ color: barColor, fontWeight: 700 }}
              >
                {row.original.positionDriftAvailable === false
                  ? '—'
                  : `+${row.original.assetPositionPercentage.toFixed(1)}%`}
              </Typography>
            </Box>
            {row.original.positionDriftAvailable === false ? (
              <Typography variant='labelSmall' color='textSecondary'>
                {t('driftQueue:table.positionUnavailable')}
              </Typography>
            ) : (
              <AssetProgressBar
                data={[
                  {
                    key: 'position',
                    percentage: row.original.assetPositionPercentage,
                    colorCode: barColor,
                  },
                ]}
              />
            )}
          </Box>
        );
      },
    },
  ];

  return columns;
}
